import { useEffect, useState } from "react";
import { ApiError, auth, type Me } from "./api";
import { Shell } from "./components/Shell";
import { Banner, Button, Field, Spinner } from "./components/ui";

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [booting, setBooting] = useState(true);
  // Lien d'invitation : ?token=… → page d'acceptation (pré-auth).
  const inviteToken = new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    auth
      .me()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setBooting(false));
  }, []);

  if (booting)
    return (
      <div className="login">
        <Spinner label="Ouverture de la session…" />
      </div>
    );
  if (!me && inviteToken) return <AcceptInvite token={inviteToken} onAccepted={setMe} />;
  if (!me) return <Login onSignedIn={setMe} />;
  return <Shell me={me} onSignOut={() => setMe(null)} />;
}

// Page d'acceptation d'invitation (PUBLIQUE) : le coéquipier choisit son nom +
// mot de passe ; le backend crée le compte et auto-login (cookie).
function AcceptInvite({ token, onAccepted }: { token: string; onAccepted: (me: Me) => void }) {
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await auth.acceptInvite(token, displayName.trim(), password);
      // Nettoie le token de l'URL puis bascule sur la session créée.
      window.history.replaceState({}, "", window.location.pathname);
      const me = await auth.me();
      if (me) onAccepted(me);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? "Invitation invalide ou expirée."
          : "Échec de l'acceptation.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <form className="login-card" onSubmit={submit}>
        <h1 className="login-brand">Rejoindre Agency OS</h1>
        <p className="login-note">Tu as été invité·e. Choisis ton nom et un mot de passe.</p>
        <Field
          label="Nom"
          placeholder="ton nom"
          autoComplete="name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
        />
        <Field
          label="Mot de passe"
          type="password"
          placeholder="mot de passe"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && <Banner tone="danger">{error}</Banner>}
        <Button type="submit" disabled={busy || !displayName.trim() || !password}>
          {busy ? <Spinner label="Création du compte…" /> : "Rejoindre"}
        </Button>
      </form>
    </div>
  );
}

function Login({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await auth.login(email.trim(), password);
      const me = await auth.me();
      if (me) onSignedIn(me);
      else throw new ApiError(500, "session_not_established");
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? "Identifiants invalides."
          : "Échec de connexion.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <form className="login-card" onSubmit={submit}>
        <h1 className="login-brand">Agency OS</h1>
        <Field
          label="E-mail"
          type="email"
          placeholder="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Field
          label="Mot de passe"
          type="password"
          placeholder="mot de passe"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && <Banner tone="danger">{error}</Banner>}
        <Button type="submit" disabled={busy}>
          {busy ? <Spinner label="Connexion…" /> : "Se connecter"}
        </Button>
      </form>
    </div>
  );
}
